import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import Modal from './Modal';
import Spinner from './spinner/Spinner';

function DeleteCohortWithStudents({ cohortId, cohortName, onClose }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState(1);

  const confirmationPhrase = `DELETE ${cohortName}`;

  const deleteCohortMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstancev2.delete(
        `/cohorts/${cohortId}/delete-with-students`,
        {
          data: { confirm_deletion: true },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(
        data.success_message ||
          `Cohort deleted successfully. ${data.students_deleted} students removed.`
      );
      queryClient.invalidateQueries(['cohorts']);
      onClose();
      navigate('/cohorts');
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          error.message ||
          'Failed to delete cohort'
      );
    },
  });

  const handleProceedToConfirm = () => {
    setStep(2);
  };

  const handleDelete = () => {
    if (confirmText !== confirmationPhrase) {
      toast.error('Confirmation text does not match. Please try again.');
      return;
    }
    deleteCohortMutation.mutate();
  };

  return (
    <Modal modalOpen closeModal={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-xl p-6">
        {step === 1 && (
          <>
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-600">
                  Danger: Delete Cohort
                </h2>
                <p className="text-gray-600 text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-red-800 mb-2">
                You are about to delete:
              </h3>
              <p className="text-lg font-bold text-gray-800 mb-3">
                {cohortName}
              </p>

              <h4 className="font-semibold text-red-800 mb-2">
                This will permanently delete:
              </h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>The cohort and all its settings</li>
                <li>All student accounts in this cohort</li>
                <li>Student education backgrounds</li>
                <li>Student work experiences</li>
                <li>Group memberships</li>
                <li>Onboarding configurations</li>
                <li>Certificate settings</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium">
                ⚠️ Students will be able to re-register for a new cohort after
                deletion.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProceedToConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                I Understand, Proceed
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-600">
                  Final Confirmation
                </h2>
                <p className="text-gray-600 text-sm">
                  Type the confirmation phrase to proceed
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                To confirm deletion, please type:
              </p>
              <p className="font-mono bg-gray-100 text-red-600 p-2 rounded text-center font-bold">
                {confirmationPhrase}
              </p>
            </div>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirmation phrase here..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={
                  confirmText !== confirmationPhrase ||
                  deleteCohortMutation.isPending
                }
                className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                  confirmText === confirmationPhrase &&
                  !deleteCohortMutation.isPending
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {deleteCohortMutation.isPending ? (
                  <>
                    <Spinner size="small" />
                    Deleting...
                  </>
                ) : (
                  'Delete Cohort Permanently'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default DeleteCohortWithStudents;
